import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface PricingCardsProps {
  annual: boolean;
}

export default function PricingCards({ annual }: PricingCardsProps) {
  const { t } = useTranslation();

  const plans = [
    {
      key: "plan1",
      name: t("pricing.plan1_name"),
      desc: t("pricing.plan1_desc"),
      price: annual ? t("pricing.plan1_price_annual") : t("pricing.plan1_price_monthly"),
      isFree: true,
      popular: false,
      color: "border-gray-200",
      btnClass: "border border-gray-300 text-gray-700 hover:border-gray-500 hover:bg-gray-50",
      btnLabel: t("pricing.get_started"),
      features: [
        { label: "1 Email ünvanı", included: true },
        { label: "5 GB yaddaş", included: true },
        { label: "FİN doğrulaması", included: true },
        { label: "SSL şifrələmə", included: true },
        { label: "Spam filtri", included: true },
        { label: "Mobil giriş", included: true },
        { label: "Xüsusi domen", included: false },
        { label: "Prioritet dəstək", included: false },
        { label: "Çoxlu istifadəçi", included: false },
        { label: "API çıxışı", included: false },
      ],
    },
    {
      key: "plan2",
      name: t("pricing.plan2_name"),
      desc: t("pricing.plan2_desc"),
      price: annual ? t("pricing.plan2_price_annual") : t("pricing.plan2_price_monthly"),
      isFree: false,
      popular: true,
      color: "border-red-500",
      btnClass: "bg-red-600 text-white hover:bg-red-700",
      btnLabel: t("pricing.get_started"),
      features: [
        { label: "10 Email ünvanı", included: true },
        { label: "50 GB yaddaş", included: true },
        { label: "FİN doğrulaması", included: true },
        { label: "SSL + E2E şifrələmə", included: true },
        { label: "Spam + Antivirus filtri", included: true },
        { label: "Mobil giriş", included: true },
        { label: "Xüsusi domen", included: true },
        { label: "Prioritet dəstək", included: true },
        { label: "Çoxlu istifadəçi", included: false },
        { label: "API çıxışı", included: false },
      ],
    },
    {
      key: "plan3",
      name: t("pricing.plan3_name"),
      desc: t("pricing.plan3_desc"),
      price: annual ? t("pricing.plan3_price_annual") : t("pricing.plan3_price_monthly"),
      isFree: false,
      popular: false,
      color: "border-gray-800",
      btnClass: "bg-gray-900 text-white hover:bg-gray-700",
      btnLabel: t("pricing.contact_sales"),
      features: [
        { label: "Limitsiz email ünvanı", included: true },
        { label: "Limitsiz yaddaş", included: true },
        { label: "FİN doğrulaması", included: true },
        { label: "Tam şifrələmə paketi", included: true },
        { label: "Tam təhlükəsizlik paketi", included: true },
        { label: "Mobil giriş", included: true },
        { label: "Xüsusi domen", included: true },
        { label: "24/7 dəstək + SLA", included: true },
        { label: "Çoxlu istifadəçi idarəsi", included: true },
        { label: "API çıxışı", included: true },
      ],
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
      {plans.map((plan) => (
        <div
          key={plan.key}
          className={`relative bg-white rounded-2xl border-2 ${plan.color} p-7 flex flex-col transition-transform hover:-translate-y-1`}
        >
          {/* Popular badge */}
          {plan.popular && (
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
              <span className="bg-red-600 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                {t("pricing.popular")}
              </span>
            </div>
          )}

          {/* Plan name + desc */}
          <div className="mb-5">
            <h3 className="text-lg font-bold text-gray-900 mb-1">{plan.name}</h3>
            <p className="text-sm text-gray-400 leading-relaxed">{plan.desc}</p>
          </div>

          {/* Price */}
          <div className="mb-6 pb-6 border-b border-gray-100">
            {plan.isFree ? (
              <div className="text-3xl font-extrabold text-gray-900">{plan.price}</div>
            ) : (
              <div className="flex items-end gap-1">
                <span className="text-4xl font-extrabold text-gray-900">{plan.price}</span>
                <span className="text-gray-400 text-sm mb-1.5">{t("pricing.per_month")}</span>
              </div>
            )}
            {!plan.isFree && annual && (
              <p className="text-xs text-green-600 font-medium mt-1">
                <i className="ri-price-tag-3-line mr-1" />
                İllik ödənişlə — 2 ay pulsuz
              </p>
            )}
          </div>

          {/* Features */}
          <ul className="space-y-3 mb-8 flex-1">
            {plan.features.map((f) => (
              <li key={f.label} className="flex items-center gap-2.5">
                <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0`}>
                  {f.included ? (
                    <i className="ri-checkbox-circle-fill text-green-500 text-base" />
                  ) : (
                    <i className="ri-close-circle-line text-gray-300 text-base" />
                  )}
                </div>
                <span className={`text-sm ${f.included ? "text-gray-700" : "text-gray-300"}`}>
                  {f.label}
                </span>
              </li>
            ))}
          </ul>

          {/* CTA Button */}
          <Link
            to="/register"
            className={`w-full py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap text-center ${plan.btnClass}`}
          >
            {plan.btnLabel}
          </Link>
        </div>
      ))}
    </div>
  );
}
